// Tamil-Katral-Maiyam\src\components\landing\MetricCard.tsx

import React, { useEffect, useState } from "react";
import { useInView } from "../../hooks/useInView";
import { clsx } from "clsx";

interface MetricCardProps {
  label: string;
  value: number;
  suffix?: string;
  delay?: 0 | 1 | 2 | 3;

  // ✅ NEW: make inner card glass/transparent when needed
  transparent?: boolean;
}

function useCountUp(end: number, inView: boolean, duration = 1500) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start: number;
    const step = (timestamp: number) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      const progress = Math.min(elapsed / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [end, inView, duration]);

  return count;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  suffix = "",
  transparent = false,
}) => {
  const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });
  const count = useCountUp(value, inView);

  return (
    <div
      ref={ref}
      className={clsx(
        // ✅ make all metric cards equal height
        "h-full min-h-[140px] flex flex-col items-center justify-center",
        "rounded-xl p-5 text-center transition-all duration-700 ease-out",
        "opacity-0 translate-y-4 data-[visible=true]:opacity-100 data-[visible=true]:translate-y-0",

        // ✅ DEFAULT (existing look)
        !transparent &&
          "border border-app-border dark:border-gray-700 bg-app-surface dark:bg-gray-800/80 shadow-soft",

        // ✅ TRANSPARENT (glass look)
        transparent &&
          "border border-white/22 dark:border-white/14 bg-white/14 dark:bg-white/6 backdrop-blur-sm shadow-[0_10px_26px_rgba(0,0,0,0.12)]"
      )}
      data-visible={inView}
    >
      <p className="text-2xl sm:text-3xl font-bold text-primary-600 dark:text-primary-400 tabular-nums">
        {count}
        {suffix}
      </p>

      {/* ✅ clamp label so it never changes height */}
      <p className="mt-2 text-sm font-medium text-app-text-muted dark:text-gray-400 line-clamp-2">
        {label}
      </p>
    </div>
  );
};
